<script setup lang="ts">
import type { NotificationItem, NotificationListContent } from "../types/notifications";
import NotificationsAPI from "~/services/notifications";
import formatDate from "~/utils/format-date";
import getErrorLocalePathMessage from "~/utils/locale";
import CardPlayerJersey from "./Card/PlayerJersey/index.vue";

const emit = defineEmits<{
  clearAll: [];
  markAllAsRead: [];
  markAsRead: [id: string];
  close: [];
}>();

const { locale, t, te } = useI18n();
const isMobile = useIsMobile();
const toaster = useToast();

const hideDropdownRef = ref<(() => void) | null>(null);
const scrollContainerRef = ref<HTMLElement | null>(null);

// Pagination state
const currentPage = ref(0);
const pageSize = 10;
const loadingNotifications = ref(false);
const loadingMore = ref(false);
const loadingClearAll = ref(false);
const hasMore = ref(true);

// Notifications state - using reactive ref for optimistic updates
const notificationsList = ref<NotificationItem[]>([]);
const notificationsMeta = ref<Omit<NotificationListContent, "entries"> | null>(null);

const unreadCount = computed(() => {
  return notificationsList.value.filter(n => !n.read).length;
});

const hasNotifications = computed(() => {
  return notificationsList.value.length > 0;
});

const handleError = (error: unknown): void => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const { message } = getErrorLocalePathMessage(errorMessage, { t, te });
  toaster.error(message);
};

// Load initial notifications
const loadNotifications = async (page: number = 0, append: boolean = false): Promise<void> => {
  if (loadingNotifications.value || loadingMore.value) {
    return;
  }

  if (append) {
    loadingMore.value = true;
  }
  else {
    loadingNotifications.value = true;
  }

  try {
    const response = await NotificationsAPI.load({
      page,
      size: pageSize,
      next: true,
    });

    const content = response.content;

    if (append) {
      notificationsList.value = [...notificationsList.value, ...content.entries];
    }
    else {
      notificationsList.value = content.entries;
    }

    notificationsMeta.value = {
      startIndex: content.startIndex,
      itemsPerPage: content.itemsPerPage,
      totalCount: content.totalCount,
      entryCount: content.entryCount,
      count: content.count,
      limit: null,
      skip: null,
      nextExists: content.nextExists,
    };

    hasMore.value = content.nextExists ?? false;
    currentPage.value = page;
  }
  catch (error) {
    handleError(error);
  }
  finally {
    loadingNotifications.value = false;
    loadingMore.value = false;
  }
};

// Load more notifications (next page)
const loadMoreNotifications = async (): Promise<void> => {
  if (!hasMore.value || loadingMore.value || loadingNotifications.value) {
    return;
  }

  await loadNotifications(currentPage.value + 1, true);
};

// Handle scroll to detect when user reaches bottom
const handleScroll = (): void => {
  const container = scrollContainerRef.value;
  if (!container) {
    return;
  }

  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;
  const threshold = 150; // Load more when 150px from bottom

  if (scrollTop + clientHeight >= scrollHeight - threshold && hasMore.value && !loadingMore.value && !loadingNotifications.value) {
    loadMoreNotifications();
  }
};

// Attach scroll event listener when ref is available
watchEffect(() => {
  const container = scrollContainerRef.value;
  if (container) {
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }
});

// Optimistically update notification read state
const optimisticallyMarkAsRead = (notificationId: string): void => {
  const notification = notificationsList.value.find(n => n._id === notificationId);
  if (notification && !notification.read) {
    notification.read = true;
  }
};

// Revert optimistic update (in case of error)
const revertMarkAsRead = (notificationId: string): void => {
  const notification = notificationsList.value.find(n => n._id === notificationId);
  if (notification) {
    notification.read = false;
  }
};

const formatNotificationDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return formatDate(date, "hh:mm a MMMM d yyyy", locale.value as TAvailableLocale);
  }
  catch {
    return "";
  }
};

// Get team name with preference for shortName, fallback to longName
const getTeamName = (longName?: string, shortName?: string): string => {
  return shortName || longName || "";
};

// Construct and format notification title with bold quoted parts
const formatNotificationTitle = (notification: NotificationItem): string => {
  const { options } = notification;

  // Build the title parts
  const playerHighlight = `Player #${options.playerNumber} Highlight`;

  // Get team names with preference for shortName
  const homeTeam = getTeamName(options.homeTeamName, options.homeTeamShortName);
  const awayTeam = getTeamName(options.awayTeamName, options.awayTeamShortName);

  // Build the game title
  const gameTitle = homeTeam && awayTeam ? `${homeTeam} vs. ${awayTeam}` : (options.eventTitle || "");

  // Construct the full title with quoted parts
  const title = `"${playerHighlight}" from the game "${gameTitle}" is now ready!`;

  // Format quoted parts as bold
  return title.replace(/["']([^"']+)["']/g, "<strong>$1</strong>");
};

const handleClose = (hide: () => void): void => {
  hide();
  emit("close");
};

const handleMarkAllAsRead = async (): Promise<void> => {
  if (loadingNotifications.value || loadingMore.value) {
    return;
  }

  // Optimistically mark all as read
  notificationsList.value.forEach((notification) => {
    if (!notification.read) {
      notification.read = true;
    }
  });

  try {
    await NotificationsAPI.markAllAsRead();
    emit("markAllAsRead");
  }
  catch (error) {
    // Revert optimistic update on error
    await loadNotifications(0, false);
    handleError(error);
  }
};

const handleClearAll = async (): Promise<void> => {
  if (loadingClearAll.value || loadingNotifications.value || loadingMore.value) {
    return;
  }

  loadingClearAll.value = true;

  try {
    await NotificationsAPI.clearAll();
    // Clear the notifications list on success
    notificationsList.value = [];
    notificationsMeta.value = null;
    currentPage.value = 0;
    hasMore.value = true;
    emit("clearAll");
  }
  catch (error) {
    handleError(error);
  }
  finally {
    loadingClearAll.value = false;
  }
};

const handleNotificationClick = async (notification: NotificationItem): Promise<void> => {
  if (notification.read || loadingNotifications.value || loadingMore.value) {
    return;
  }

  // Optimistically update the notification
  optimisticallyMarkAsRead(notification._id);

  try {
    await NotificationsAPI.markAsRead(notification._id);
    emit("markAsRead", notification._id);
    // Refetch notifications from the beginning to ensure unreadCount is accurate
    await loadNotifications(0, false);
  }
  catch (error) {
    // Revert optimistic update on error
    revertMarkAsRead(notification._id);
    handleError(error);
  }
};

const handleShow = async (): Promise<void> => {
  if (isMobile.value) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  if (notificationsList.value.length === 0) {
    await loadNotifications(0, false);
  }
};

const handleHide = (): void => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
  // Reset pagination state when closing
  currentPage.value = 0;
  hasMore.value = true;
  emit("close");
};

const handleMobileClose = (): void => {
  if (hideDropdownRef.value) {
    handleClose(hideDropdownRef.value);
  }
};

const initializeHideFunction = (hide: () => void): void => {
  hideDropdownRef.value = hide;
};
</script>

<template>
  <UMenu
    ref="menuRef"
    :dropdown="{
      placement: isMobile ? 'bottom' : 'bottom-end',
      distance: isMobile ? 0 : 14,
    }"
    @show="handleShow"
    @hide="handleHide"
  >
    <template #trigger="{ toggle }">
      <slot
        :toggle="toggle"
      />
    </template>

    <template #default="{ hide }">
      <div
        v-if="(initializeHideFunction(hide), true)"
        class="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-[28rem] max-h-[34rem] flex flex-col overflow-hidden"
        :class="{
          'fixed inset-0 rounded-none max-w-full max-h-full z-50': isMobile,
        }"
        role="dialog"
        aria-labelledby="notifications-title"
        aria-modal="true"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 pt-6"
          :style="{ borderColor: 'var(--base-header-line-color, #e5e7eb)' }"
        >
          <h2 id="notifications-title" class="text-xl font-bold text-black dark:text-white">
            Notifications
          </h2>
          <button
            v-if="isMobile"
            type="button"
            class="flex items-center justify-center w-8 h-8 -mr-2 cursor-pointer hover:opacity-70 transition-opacity"
            aria-label="Close notifications"
            @click="handleMobileClose"
          >
            <IconClose
              :width="20"
              :height="20"
              fill="var(--base-icon-color, #A9B7D1)"
            />
          </button>
        </div>

        <!-- Notifications List -->
        <div
          ref="scrollContainerRef"
          class="flex-1 overflow-y-auto min-h-0 relative"
        >
          <!-- Clearing All Overlay -->
          <div
            v-if="loadingClearAll"
            class="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-10"
          >
            <div class="flex flex-col items-center gap-3">
              <div class="w-8 h-8 border-4 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-400 rounded-full animate-spin" />
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                Clearing notifications...
              </p>
            </div>
          </div>
          <!-- Skeleton Loading -->
          <div v-if="loadingNotifications && notificationsList.length === 0" class="flex flex-col">
            <div
              v-for="i in 3"
              :key="`skeleton-${i}`"
              class="border-b last:border-b-0"
              :style="{ borderColor: 'var(--base-header-line-color, #e5e7eb)' }"
            >
              <div class="flex items-start gap-3 p-6">
                <!-- Player Jersey Skeleton -->
                <div class="flex-shrink-0 skeletonLoading rounded" style="width: 53.2px; height: 77px;" />

                <!-- Content Skeleton -->
                <div class="flex-1 min-w-0 space-y-2">
                  <div class="flex items-start gap-2">
                    <!-- Title lines -->
                    <div class="flex-1 space-y-2">
                      <div class="skeletonLoading h-4 rounded w-full" />
                      <div class="skeletonLoading h-4 rounded w-5/6" />
                      <div class="skeletonLoading h-4 rounded w-4/6" />
                    </div>
                    <!-- Unread indicator skeleton -->
                    <div class="skeletonLoading w-2 h-2 rounded-full flex-shrink-0 mt-[0.45rem]" />
                  </div>
                  <!-- Date skeleton -->
                  <div class="skeletonLoading h-3 rounded w-32" />
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!hasNotifications" class="flex items-center h-[25rem] justify-center py-12 px-4" role="status" aria-live="polite">
            <p class="text-base font-medium text-gray-500 dark:text-gray-400">
              No notifications yet
            </p>
          </div>

          <UList v-else-if="notificationsList.length > 0" class="flex flex-col !p-0" role="list">
            <div
              v-for="notification in notificationsList"
              :key="notification._id"
              role="listitem"
              class="border-b last:border-b-0 transition-colors"
              :class="{
                'cursor-pointer': !notification.read,
                'bg-[rgba(var(--base-primary-color-rgb,59,130,246),0.02)]': !notification.read,
                'hover:!bg-gray-100 dark:hover:!bg-gray-800': !notification.read,
              }"
              :style="{
                borderColor: 'var(--base-header-line-color, #e5e7eb)',
              }"
              :aria-label="notification.read ? 'Read notification' : 'Unread notification'"
              @click="handleNotificationClick(notification)"
            >
              <div class="flex items-start gap-3 p-6">
                <div class="relative flex-shrink-0" style="width: 53.2px; height: 77px; overflow: hidden;">
                  <div style="transform: scale(0.7); transform-origin: left top;">
                    <CardPlayerJersey :player-number="notification.options.playerNumber" :color="notification.options.teamColor" />
                  </div>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p
                      class="font-medium line-clamp-3 text-black dark:text-white leading-[1.4] flex-1 min-w-0"
                      v-html="formatNotificationTitle(notification)"
                    />
                  </div>
                  <p class="text-xs text-gray-500 font-medium dark:text-gray-400 mt-1">
                    {{ formatNotificationDate(notification.created) }}
                  </p>
                </div>

                <div
                  v-if="!notification.read"
                  class="w-2 h-2 rounded-full flex-shrink-0 self-center"
                  :style="{ backgroundColor: 'var(--base-primary-color, #3b82f6)' }"
                  aria-label="Unread"
                />
              </div>
            </div>

            <!-- Loading More Indicator -->
            <div
              v-if="loadingMore"
              class="flex items-center justify-center py-4 border-b"
              :style="{ borderColor: 'var(--base-header-line-color, #e5e7eb)' }"
            >
              <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <div class="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                <span>Loading more...</span>
              </div>
            </div>
          </UList>
        </div>

        <!-- Footer -->
        <div
          v-if="hasNotifications"
          class="flex items-center justify-between gap-4 p-6 border-t"
          :style="{ borderColor: 'var(--base-header-line-color, #e5e7eb)' }"
        >
          <BaseButton
            name="clear-all-notifications"
            title="Clear all"
            type="secondary"
            link
            :disabled="loadingNotifications || loadingMore || loadingClearAll"
            class="flex items-center gap-2"
            :style="{ color: 'var(--base-primary-color, #3b82f6)' }"
            @click="handleClearAll"
          >
            <div class="flex items-center gap-2">
              <IconClose :width="12" :height="12" fill="var(--base-primary-color)" />
              <span class="font-medium">Clear all</span>
            </div>
          </BaseButton>
          <BaseButton
            v-if="unreadCount !== 0"
            name="mark-all-read-notifications"
            title="Mark all as read"
            type="secondary"
            link
            :disabled="loadingNotifications || loadingMore || loadingClearAll"
            class="flex items-center gap-2"
            :style="{ color: 'var(--base-primary-color, #3b82f6)' }"
            @click="handleMarkAllAsRead"
          >
            <div class="flex items-center gap-2">
              <IconCheck :width="12" :height="12" fill="var(--base-primary-color)" />
              <span class="font-medium">Mark all as read</span>
            </div>
          </BaseButton>
        </div>
      </div>
    </template>
  </UMenu>
</template>
